import React, { useState } from "react";
import { Link } from "react-router-dom";
import { LOGO } from "@/assets";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/accountRole";
import { Menu, X, ChevronDown } from "lucide-react";
import LoginDropdown from "@/components/LoginDropdown";
import { HeaderRightCluster, HeaderActions } from "@/components/HeaderControls";
import MobileMenu from "@/components/MobileMenu";
import { FEEDBACK_EMAIL } from "@/lib/feedback";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// SINGLE shared member header, rendered identically on EVERY non-admin page (landing included).
// The center nav is FOUR top-level items — About ▾, Forum, Our Partners ▾, Marketplace — with two
// click-dropdowns grouping the former links. Logo, colours, fonts and hover treatment are unchanged.
const ABOUT_ITEMS = [
  { label: "Pricing", to: "/pricing", testid: "nav-about-pricing" },
  { label: "Swag", to: "/swag", testid: "nav-about-swag" },
  { label: "In The Wild", to: "/in-the-wild", testid: "nav-about-in-the-wild" },
  { label: "FAQ", to: "/faq", testid: "nav-about-faq" },
];
const PARTNER_ITEMS = [
  { label: "Ambassadors", to: "/ambassadors", testid: "nav-partners-ambassadors" },
  { label: "Business Partners", to: "/partners", testid: "nav-partners-business" },
  { label: "User Map", to: "/map", testid: "nav-partners-map" },
];

// Identical treatment as before: same size/weight, muted-grey (text-secondary-foreground), hover
// only — no active-page highlight, no pills/tabs. Dropdown triggers reuse the exact same class.
const LINK_CLS = "py-1 text-[12.5px] font-medium font-mono uppercase tracking-[0.06em] text-secondary-foreground hover:text-foreground transition-colors whitespace-nowrap";
const TRIGGER_CLS = `${LINK_CLS} inline-flex items-center gap-1 outline-none data-[state=open]:text-foreground`;
const MENU_ITEM_CLS = "cursor-pointer font-mono uppercase text-[12px] tracking-[0.06em] text-secondary-foreground focus:bg-white/[0.04] focus:text-foreground data-[highlighted]:bg-white/[0.04] data-[highlighted]:text-foreground";

function NavDropdown({ label, testid, children }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid={testid} className={TRIGGER_CLS}>
        {label} <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={10} className="bg-surface border-sniper text-foreground min-w-[190px] rounded-none" data-testid={`${testid}-menu`}>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Lockup({ to = "/" }) {
  return (
    <Link to={to} data-testid="nav-logo" aria-label="OpticSwap home" className="flex items-center gap-2.5 shrink-0 group">
      <img src={LOGO} alt="OpticSwap" className="h-9 w-auto group-hover:opacity-90 transition-opacity" />
      <div className="hidden sm:block leading-none" data-testid="nav-lockup-text">
        <div className="font-heading font-bold uppercase text-[18px] tracking-tight text-foreground leading-none">Buy · Sell · Trade</div>
        <div className="font-mono uppercase text-[8.5px] tracking-[0.18em] text-volt mt-[3px]">Verified Optics Marketplace</div>
      </div>
    </Link>
  );
}

export default function Navbar() {
  const { user, adminMode } = useAuth();
  const [open, setOpen] = useState(false);
  const admin = isAdminRole(user?.role);
  // Only the (future) impersonation banner is a full 36px bar that pushes content down;
  // ADMIN MODE is a 3px fixed rail that overlays harmlessly, so the navbar stays at top-0.
  const offsetTop = admin && adminMode === "impersonate" ? "top-9" : "top-0";

  return (
    <header className={`sticky ${offsetTop} z-50 bg-obsidian border-b border-sniper`}>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 min-h-16 py-2.5 flex items-center gap-6">
        <Lockup to={user ? "/feed" : "/"} />

        {/* Center nav — 4 top-level items: About ▾, Forum, Our Partners ▾, Marketplace. Same
            fonts/colors/spacing + hover as before; two click-dropdowns group the former links.
            Collapses to the hamburger below 768px. */}
        <nav className="hidden md:flex flex-1 items-center justify-center gap-x-7 lg:gap-x-9" data-testid="nav-desktop">
          <NavDropdown label="About" testid="nav-about">
            {ABOUT_ITEMS.map((n) => (
              <DropdownMenuItem key={n.to} asChild className={MENU_ITEM_CLS}>
                <Link to={n.to} data-testid={n.testid}>{n.label}</Link>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="bg-sniper" />
            <DropdownMenuItem asChild className={MENU_ITEM_CLS}>
              <a href={`mailto:${FEEDBACK_EMAIL}`} data-testid="nav-about-contact">Contact Us</a>
            </DropdownMenuItem>
          </NavDropdown>
          <Link to="/forum" data-testid="nav-forum" className={LINK_CLS}>Forum</Link>
          <NavDropdown label="Our Partners" testid="nav-partners">
            {PARTNER_ITEMS.map((n) => (
              <DropdownMenuItem key={n.to} asChild className={MENU_ITEM_CLS}>
                <Link to={n.to} data-testid={n.testid}>{n.label}</Link>
              </DropdownMenuItem>
            ))}
          </NavDropdown>
          <Link to="/feed" data-testid="nav-marketplace" className={LINK_CLS}>Marketplace</Link>
        </nav>

        {/* RIGHT — logged-in: the folded action set (+POST/Marketplace · feedback · messages ·
            forum · mod) spaced out with real gaps alongside Admin + avatar. Logged-out: login + create. */}
        <div className="hidden md:flex items-center gap-4 shrink-0" data-testid="nav-cluster">
          {user ? (
            <>
              <HeaderActions />
              <HeaderRightCluster />
            </>
          ) : (
            <>
              <LoginDropdown />
              <Link to="/join" data-testid="nav-register" className="bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-[13px] px-4 py-2 transition-colors whitespace-nowrap">
                Create account
              </Link>
            </>
          )}
        </div>

        {/* MOBILE right side — the whole authed header on ONE line: actions then the hamburger. */}
        <div className="md:hidden flex items-center gap-1.5 ml-auto" data-testid="nav-mobile-actions">
          {user && <HeaderActions compact />}
          <button className="relative text-foreground p-1 -mr-1" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </header>
  );
}
